import { useCallback, useState } from 'react';

import { useSafeAuth } from '@opendoor/auth-fe/context';
import { GENERIC_AUTH_FAILURE_MESSAGE } from '@opendoor/auth-fe/errors';
import { analytics } from '@opendoor/observability/slim';

import type { IRequestStatus } from './RequestStatus';

const IDLE_STATUS: IRequestStatus = { isLoading: false };
const LOADING_STATUS: IRequestStatus = { isLoading: true };

export interface AccountFormSession {
  loggedIn: boolean;
  infoIsLoaded: boolean;
  sessionEmail: string | undefined;
  email: string;
  hasGeneratedPassword: boolean | undefined;
  humanStatusRequestStatus: IRequestStatus;
  requestHumanStatus: (email: string) => Promise<void>;
  requestResetPassword: (email: string) => void;
}

export function useAccountFormSession(): AccountFormSession {
  const {
    authentication,
    user,
    requestHumanStatus: authRequestHumanStatus,
    requestPasswordReset: authRequestPasswordReset,
  } = useSafeAuth();

  const [email, setEmail] = useState('');
  // The magic-link gate treats a boolean as resolved status; undefined keeps
  // its loader visible until the human-status lookup completes.
  const [hasGeneratedPassword, setHasGeneratedPassword] = useState<boolean | undefined>(undefined);
  const [humanStatusRequestStatus, setHumanStatusRequestStatus] =
    useState<IRequestStatus>(IDLE_STATUS);

  const requestHumanStatus = useCallback<AccountFormSession['requestHumanStatus']>(
    async (statusEmail) => {
      setHumanStatusRequestStatus(LOADING_STATUS);
      const result = await authRequestHumanStatus(statusEmail);
      if (result.success) {
        setEmail(statusEmail);
        setHasGeneratedPassword(result.data.hasGeneratedPassword);
        setHumanStatusRequestStatus({ isLoading: false, success: true });
        // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
        analytics.trackEvent(
          'session',
          'request-human-status',
          result.data.isNewHuman ? 'no-account' : 'success',
          {
            email: statusEmail,
            hasAutoGeneratedPassword: result.data.hasGeneratedPassword,
            isNewHuman: result.data.isNewHuman,
          },
        );
        return;
      }
      setHumanStatusRequestStatus({
        isLoading: false,
        error: result.error.message || GENERIC_AUTH_FAILURE_MESSAGE,
      });
      // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
      analytics.trackEvent('session', 'request-human-status', 'error', {
        email: statusEmail,
        error: result.error.message,
      });
    },
    [authRequestHumanStatus],
  );

  const requestResetPassword = useCallback<AccountFormSession['requestResetPassword']>(
    (resetEmail) => {
      void (async () => {
        const result = await authRequestPasswordReset(resetEmail);
        if (result.success) {
          const payload = {
            email: resetEmail,
            hasAutoGeneratedPassword: result.data.hasGeneratedPassword,
            isNewHuman: result.data.isNewHuman,
          };
          // Only an existing account receives a reset/setup email.
          if (!result.data.isNewHuman) {
            // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
            analytics.trackEvent('session', 'reset-password', 'reset-password', {
              ...payload,
              shouldResetPassword: true,
            });
          }
          // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
          analytics.trackEvent(
            'session',
            'reset-password',
            result.data.isNewHuman ? 'no-account' : 'success',
            payload,
          );
          return;
        }
        // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
        analytics.trackEvent('session', 'reset-password', 'error', {
          email: resetEmail,
          error: result.error.message,
        });
      })();
    },
    [authRequestPasswordReset],
  );

  return {
    loggedIn: authentication.state === 'authenticated',
    infoIsLoaded: authentication.state !== 'authenticating',
    sessionEmail: user?.email,
    email,
    hasGeneratedPassword,
    humanStatusRequestStatus,
    requestHumanStatus,
    requestResetPassword,
  };
}
